import React, { useMemo, useState } from "react";
import ActionMenu from "../../actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import notesIcon from "../../../assets/componentsIcons/dropdown/doc.svg";
import unitsIcon from "../../../assets/componentsIcons/listings/units.svg";
import vacanciesIcon from "../../../assets/componentsIcons/listings/vacancies.svg";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import {
  DisplayColorEnum,
  EntityType,
  NotesEntityTypesEnum,
  TenantService,
  TenantVM,
} from "@propertelligent/client-api";
import CalenderIcon from "../../../assets/svgs/calenderIcon";
import { Link, useNavigate } from "react-router-dom";
import apiHelper from "../../../utils/apiHelper";
import { ConfirmationPopup } from "../../confirmation-popup/confirmation-popup-demo";
import AddNotesForm from "../../notes/AddNotesForm";
import UtilHelper from "../../../utils/UtilHelper";
import { GetImages } from "../../../utils/GetImages";
import { useLeaseInfoTooltip } from "../../../hooks/useLeaseInfoTooltip";
import AddPaymentModal from "../../payments/AddPaymentModal";
import { usePermissions } from "../../../hooks/usePermissions";

import ArrowDownIcon from "../../../assets/svgs/last-transactions/leaseIcon";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import { LeaseIcon } from "../../../assets/svgs/unitIcons/leaseIcon";
import displayColorMap from "../../../utils/displayColorMap";
import { UnitIconOutline } from "../../../assets/svgs/unitIcons/unitIcon copy";
import LargeTooltip from "../../tooltips/large-tooltip";
import BluePayableIcon from "../../../assets/Payment-Icon.svg";
import { getTenantDueHighlight } from "../../../utils/tenantPastDue";

interface DataHookState {
  rows: TenantVM[];
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
}

interface CurrentTenantProps {
  Id?: string;
  className?: string;
  IsFooter?: boolean;
  dataHookState?: DataHookState;
  hasAnyTenants?: boolean;
}

const CurrentTenantCard: React.FC<CurrentTenantProps> = ({
  Id,
  className = "",
  IsFooter = true,
  dataHookState,
  hasAnyTenants = false,
}) => {
  const { rows, loading, hasMore, fetchData, currentPage } = dataHookState;

  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const { handleLeaseInfoClick, tooltipState, handleCloseTooltip } =
    useLeaseInfoTooltip();

  const [deleteState, setDeleteState] = useState({
    id: null as number | null,
    isOpen: false,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);

  const [selectedTenantIdForNotes, setSelectedTenantIdForNotes] =
    useState<any>(null);

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property,
  });

  const MENU_ITEMS = useMemo(() => {
    const items = [];

    if (canWrite("Tenant")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: (Id: number) =>
          navigate("/tenants/edit/" + Id, {
            state: { from: window.location.pathname, activeTab: "Tenants" },
          }),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (Id: number) => setDeleteState({ id: Id, isOpen: true }),
        isDivider: false,
        isDestructive: true,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Payments")) {
      items.push({
        icon: BluePayableIcon,
        label: "Add Payment",
        action: (tenantId) => {
          setPaymentModalConfig({
            isOpen: true,
            entityId: Number(tenantId),
            entityType: EntityType.Tenant,
          });
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Tenant")) {
      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: (tenantId: string) => {
          setSelectedTenantIdForNotes(tenantId);
          setModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [canWrite, navigate]);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const handleDeleteTanent = async () => {
    if (!deleteState.id || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await apiHelper(
        TenantService.deleteApiTenant,
        {
          showNotification: true,
          successMessage: "Tenant Deleted Successfully",
        },
        deleteState.id,
      );

      if (response?.data?.success) {
        setDeleteState({ id: null, isOpen: false });
        fetchData(1);
      }
    } catch (err) {
      setDeleteState({ id: null, isOpen: false });
      throw err;
    } finally {
      setIsDeleting(false);
      setDeleteState({ id: null, isOpen: false });
    }
  };

  return (
    <div className="infinite-scroll-container">
      {loading && rows.length === 0 ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : rows.length === 0 && !loading ? (
        <EmptyState
          icon={GetImages.emptyStateTenant}
          heading={hasAnyTenants ? "No Tenants Found" : "Add Your First Tenant"}
          isButton={hasAnyTenants ? false : true}
          buttonLabel={hasAnyTenants ? "Add Tenant" : "Add Tenant"}
          onButtonClick={() => navigate("/tenants/create")}
        />
      ) : (
        <>
          {rows.map((listingData, index) => {
            const leaseColor =
              displayColorMap[
                listingData?.leaseDisplayTextColor ?? DisplayColorEnum.Default
              ];
            const dueHighlight = getTenantDueHighlight(listingData);

            return (
              <div
                key={index}
                className={`listing-card current-tenant-card-container ${className}`}
              >
                <Link
                  to={`/tenants/currenttenantdetails/${listingData?.id}/${listingData?.unitId}`}
                  className="listing-link"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="listing-card-header">
                    <div className="tenant-avatar">
                      <div className="tenant-initials">
                        {UtilHelper.getInitials(
                          listingData?.firstName + " " + listingData?.lastName,
                        )}
                      </div>
                    </div>
                    <div className="listing-details">
                      <div className="listing-info-container">
                        <div className="listing-info tenant-info-container">
                          <h3 className="listing-address">
                            {listingData.firstName} {listingData.lastName}
                          </h3>
                          <p className="listing-type">
                            {listingData?.streetAddress}
                          </p>
                          <p className="listing-type">
                            {listingData?.email}
                            <span className="contact-dot"> • </span>
                            <br className="contact-break" />
                            {listingData?.formatted?.PhoneNumber}
                          </p>
                        </div>

                        <div className="financial-metrics">
                          <div className="metric">
                            <CurrencyTooltip
                              content={listingData?.formatted?.RentRaw}
                              formatted={listingData?.formatted?.Rent}
                            >
                              <span className="metric-value">
                                {listingData?.formatted?.Rent}
                              </span>
                            </CurrencyTooltip>
                            <p className="metric-label">Rent</p>
                          </div>

                          {listingData?.formatted?.LeaseTypeId && (
                            <>
                              <div className="separator-vertical" />
                              <div className="metric">
                                <p className="metric-value">
                                  {listingData.formatted.LeaseTypeId ===
                                  "Month-to-Month"
                                    ? "MTM"
                                    : listingData.formatted.LeaseTypeId}
                                </p>
                                <p className="metric-label">Lease Type</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
                {IsFooter && (
                  <>
                    <div className="separator-horizontal" />
                    <div className="listing-card-footer">
                      <div className="listing-stats">
                        <div className="stat">
                          {listingData?.formatted?.LeaseTypeId ===
                          "Month-to-Month" ? (
                            <UnitIconOutline
                              fill={leaseColor}
                              className="stat-icon remove-icon-filter"
                              width="24"
                              height="24"
                            />
                          ) : (
                            <LeaseIcon
                              fill={leaseColor}
                              className="stat-icon remove-icon-filter"
                              width="24"
                              height="24"
                            />
                          )}

                          <span
                            className="stat-text"
                            style={{ color: leaseColor }}
                          >
                            {(listingData as any)?.leaseDisplayText}

                            {/* --- DOM WRAPPER START --- */}
                            <div className="info-tooltip-wrapper">
                              <a
                                href="#"
                                className="listing-viewmore mx-2 ml-1"
                                onClick={(e) =>
                                  handleLeaseInfoClick(
                                    e,
                                    listingData,
                                    null as any,
                                  )
                                }
                              >
                                <img
                                  src={GetImages.infoBlackIcon}
                                  alt="info-icon"
                                  width={16}
                                  height={16}
                                  className="stat-icon"
                                />
                              </a>

                              {/* Inline Tooltip Rendering */}
                              {tooltipState.isOpen && 
                               tooltipState.title === "Lease Details" && 
                               (listingData.id === (tooltipState.description as any).props.listingData.id) && (
                                <LargeTooltip
                                  isOpen={true}
                                  onClose={handleCloseTooltip}
                                  description={tooltipState.description}
                                  position={tooltipState.position} // Use absolute coordinates
                                  title="Lease Details"
                                  type="tenant"
                                />
                              )}
                            </div>
                            {/* --- DOM WRAPPER END --- */}
                          </span>
                        </div>
                        {listingData?.tenancySince && (
                          <div className="stat">
                            <CalenderIcon className="stat-icon" />
                            <span className="stat-text">
                              Tenancy: {listingData.tenancySince}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="footer-actions">
                        {dueHighlight && (
                          <div
                            className="highlight-value"
                            style={{ color: "var(--color-success)" }}
                          >
                            <ArrowDownIcon
                              className="highlight-icon"
                              width="16"
                              height="16"
                              fill="var(--color-success)"
                            />
                            <span className="highlight-text">
                              {dueHighlight.isPastDue && <>Past Due:{" "}</>}
                              <CurrencyTooltip
                                content={dueHighlight.raw}
                                formatted={dueHighlight.formatted}
                              >
                                <span>{dueHighlight.formatted}</span>
                              </CurrencyTooltip>
                            </span>
                          </div>
                        )}
                        {MENU_ITEMS.length > 0 && (
                          <ActionMenu
                            menuItems={MENU_ITEMS}
                            Id={listingData.id}
                            trigger={
                              <button className="action-button">
                                <img
                                  src={dropdownIcon}
                                  alt="More options"
                                  width={20}
                                  height={20}
                                  className="action-icon"
                                />
                              </button>
                            }
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner myClassName="Icon40" />
              )}
            </div>
          )}

          {isModalOpen && (
            <AddNotesForm
              entityId={selectedTenantIdForNotes}
              entityTypeId={NotesEntityTypesEnum.Tenant}
              isModalOpen={isModalOpen}
              setModalOpen={setModalOpen}
            />
          )}

          {/* Add PAYMENT*/}
          {paymentModalConfig.isOpen && (
            <AddPaymentModal
              isOpen={paymentModalConfig.isOpen}
              onClose={() => {
                setPaymentModalConfig((prev) => ({ ...prev, isOpen: false }));
                dataHookState.fetchData(1);
              }}
              entityIdProp={paymentModalConfig.entityId || undefined}
              entityTypeIdProp={paymentModalConfig.entityType}
              defaultPaymentMode="2"
            />
          )}

          <ConfirmationPopup
            isOpen={deleteState.isOpen}
            title="Delete Tenant"
            message="Are you sure you want to delete this Tenant?"
            primaryButtonLabel="Delete"
            secondaryButtonLabel="Cancel"
            onPrimaryAction={handleDeleteTanent}
            onSecondaryAction={() =>
              setDeleteState({ id: null, isOpen: false })
            }
            onClose={() => setDeleteState({ id: null, isOpen: false })}
            isPrimaryDestructive
            isLoading={isDeleting}
            loadingLabel="Deleting..."
          />
        </>
      )}
    </div>
  );
};

export default CurrentTenantCard;
